import RCTooltip from "rc-tooltip";
import "rc-tooltip/assets/bootstrap_white.css";
import { CSSProperties, PropsWithChildren, ReactNode, useEffect, useState } from "react";
import "./Tooltip.scss";

/**
 * Decide the next controlled-visible state for a hover request.
 *
 * `isHidden` (the tooltip must not be shown at all — e.g. an overflow label whose
 * text currently fits) gates ONLY the show direction. A hide request (`requested`
 * false) is always honoured: gating hides on `!isHidden` is what stranded the
 * rc-tooltip popup on screen when a live table reload flipped `isHidden` to true
 * mid-hover, because the mouse-leave that would close it was swallowed
 * (operator-task-fe-truncated-name-tooltip-stuck-on-table-reload).
 */
export const nextTooltipVisible = (requested: boolean, isHidden?: boolean): boolean =>
    requested && !isHidden;

/**
 * Lazily-created singleton mount node for viewport-contained tooltips.
 *
 * rc-tooltip renders its popup as a `position: absolute` child of
 * `document.body` by default. When the trigger sits flush against a viewport
 * edge — e.g. the "?" help icon in the sticky comments composer, pinned to the
 * viewport bottom — the popup extends past the edge and, being absolutely
 * positioned in the initial containing block, grows the document's scrollable
 * overflow and spawns a full-page scrollbar (operator-task-fe-comment-help-
 * tooltip-viewport-overflow). A `position: fixed` container is excluded from the
 * document's scrollable-overflow calculation, so an overflowing popup inside it
 * can never grow the page. `pointer-events: none` keeps the transparent
 * full-viewport layer from intercepting clicks; the popup re-enables pointer
 * events via Tooltip.scss so in-tooltip links stay clickable.
 */
let tooltipFixedRoot: HTMLDivElement | null = null;
const getTooltipFixedRoot = (): HTMLElement => {
    if (tooltipFixedRoot && document.body.contains(tooltipFixedRoot)) {
        return tooltipFixedRoot;
    }
    tooltipFixedRoot = document.createElement("div");
    tooltipFixedRoot.setAttribute("data-tooltip-fixed-root", "");
    // z-index MUST be set on the fixed root itself. `position: fixed` creates a
    // new stacking context, so the popup's own z-index (overlayStyle zIndex, e.g.
    // 2000) only orders siblings WITHIN this container — it no longer competes in
    // the body stacking context the way it did when rc-tooltip appended the popup
    // directly to document.body. Without a z-index here the root defaults to
    // `auto` (≈0) and the tooltip paints behind positioned app content (the
    // sticky comments composer, panels, dialogs) — invisible on hover
    // (operator-task-fe-comment-help-tooltip-viewport-overflow). 2000 reproduces
    // the pre-contain popup's body-level stacking (it beats MUI dialogs at 1300).
    tooltipFixedRoot.style.cssText =
        "position:fixed;top:0;left:0;right:0;bottom:0;z-index:2000;pointer-events:none;overflow:visible;";
    document.body.appendChild(tooltipFixedRoot);
    return tooltipFixedRoot;
};

export interface TooltipProps {
    overlay: ReactNode;
    isHidden?: boolean;
    placement?: "bottom" | "top" | "left" | "right" | "bottomLeft" | "bottomRight";
    overlayClassName?: string;
    overlayStyle?: CSSProperties;
    align?: { offset?: number[]; points?: string[]; targetOffset?: number[] };
    trigger?: any[];
    hideOnWheel?: boolean;
    showArrow?: boolean;
    triggerClassName?: string;
    /**
     * Mount the popup in a fixed, page-scroll-safe container so it can never
     * grow the document / trigger full-page scrollbars when it sits against a
     * viewport edge. rc-tooltip's overflow-adjust still positions it within the
     * viewport; this only changes the mount node.
     */
    containInViewport?: boolean;
}

type TooltipWithChildrenProps = PropsWithChildren<TooltipProps>;

const Tooltip = ({
    overlay,
    isHidden,
    children,
    placement,
    overlayClassName,
    overlayStyle = {},
    align = {},
    trigger,
    hideOnWheel,
    showArrow = true,
    triggerClassName,
    containInViewport,
}: TooltipWithChildrenProps) => {
    const [visible, setVisible] = useState(false);
    const onVisibleChange = (requested: boolean) =>
        setVisible(nextTooltipVisible(requested, isHidden));

    // Force-close if the tooltip becomes hidden while still open. This is the
    // stranded-popup case: during a live table reload the anchored row is
    // re-measured (now non-overflowing) or reused for a different record, so
    // `isHidden` flips to true — but the popup was already `visible` and no
    // mouse-leave fires on the reused instance to dismiss it. Without this the
    // rc-tooltip portal is left orphaned on screen pointing at nothing
    // (operator-task-fe-truncated-name-tooltip-stuck-on-table-reload).
    useEffect(() => {
        if (isHidden) {
            setVisible(false);
        }
    }, [isHidden]);

    const overlayWrapper = hideOnWheel ? (
        <div onWheel={() => setVisible(false)}>{overlay}</div>
    ) : (
        overlay
    );

    return (
        <RCTooltip
            overlay={overlayWrapper}
            placement={placement ?? "bottom"}
            visible={visible}
            arrowContent={<div className="rc-tooltip-arrow-inner"></div>}
            onVisibleChange={onVisibleChange}
            overlayClassName={overlayClassName}
            overlayStyle={overlayStyle}
            align={align}
            trigger={trigger}
            showArrow={showArrow}
            getTooltipContainer={containInViewport ? getTooltipFixedRoot : undefined}
        >
            <span
                onClick={() => setVisible(false)}
                className={
                    triggerClassName
                        ? `tooltipTrigger ${triggerClassName}`
                        : "tooltipTrigger"
                }
            >
                {children}
            </span>
        </RCTooltip>
    );
};

export default Tooltip;
